/**
 * Country rules for the "Ingresar nueva cuenta bancaria" form.
 *
 * The three countries do not accept the same account. CL takes a RUT and three
 * account types; CO also needs the document type and only allows two; MX pays
 * by CLABE, needs a city, and calls its account types BANK and EDENRED. Keeping
 * that here rather than inside the screen means the shape of each request body
 * is testable without rendering anything - and this repo has no React Testing
 * Library, so a rule that lives inside a component is a rule with no test.
 *
 * The server is still the authority on whether a document is valid: replicating
 * the RUT, NIT and RFC algorithms here would be a second implementation that
 * drifts. What runs client-side is only what stops an obviously incomplete or
 * mistyped form from making a round trip.
 */

export const COUNTRIES = ["CL", "CO", "MX"];

/** Account types, keyed by country, matching each `BankAccountTypes` enum. */
export const ACCOUNT_TYPES = {
  CL: [
    { value: "CURRENT", label: "Cuenta Corriente" },
    { value: "SIGHT", label: "Cuenta Vista" },
    { value: "SAVING", label: "Cuenta de Ahorro" },
  ],
  CO: [
    { value: "CURRENT", label: "Cuenta Corriente" },
    { value: "SAVING", label: "Cuenta de Ahorro" },
  ],
  MX: [
    { value: "BANK", label: "Cuenta bancaria" },
    { value: "EDENRED", label: "Edenred" },
  ],
};

/** Identity document types. Only CO stores one; CL is always a RUT, MX an RFC. */
export const DOCUMENT_TYPES = {
  CO: [
    { value: "CC", label: "Cédula de ciudadanía" },
    { value: "CE", label: "Cédula de extranjería" },
    { value: "NIT", label: "NIT" },
    { value: "PASSPORT", label: "Pasaporte" },
    { value: "SE", label: "Documento especial" },
  ],
};

/** Label for the document field, so the operator knows what is expected. */
export const DOCUMENT_LABEL = {
  CL: "RUT del titular",
  CO: "Documento del titular",
  MX: "RFC del titular",
};

export const DOCUMENT_PLACEHOLDER = {
  CL: "11111111-1",
  CO: "1311311314",
  MX: "AAA010101AAA",
};

/** Fields that exist only in some countries. */
export const hasDocumentType = (country) => country === "CO";
export const hasClabe = (country) => country === "MX";
export const hasCity = (country) => country === "MX";

const MX_ACCOUNT_NUMBER_MAX_LENGTH = 18;
const CLABE_LENGTH = 18;

export const emptyBankAccountForm = () => ({
  personId: "",
  identifier: "",
  bankId: "",
  accountType: "",
  accountNumber: "",
  clabeNumber: "",
  holder: "",
  document: "",
  documentType: "",
  email: "",
  city: "",
});

const HTML_ESCAPES = {
  "&": "&amp;",
  "<": "&lt;",
  ">": "&gt;",
  '"': "&quot;",
  "'": "&#39;",
};

/**
 * Escape a value for interpolation into the confirmation dialog's markup.
 *
 * The dialog uses SweetAlert's `html` so it can bold the account being
 * registered, which means every interpolated value is parsed as markup. The
 * holder and the document are free text, and the bank label comes from the
 * API, so a value like `<img src=x onerror=...>` would execute in the admin
 * origin. `text` is not an option here without losing the emphasis that makes
 * the dialog readable, so the values are escaped instead.
 */
export const escapeHtml = (value) =>
  String(value ?? "").replace(/[&<>"']/g, (char) => HTML_ESCAPES[char]);

/** Digits only, once the separators people type are removed. */
const digitsOnly = (value) => String(value ?? "").replace(/[-\s.]/g, "");

const isBlank = (value) => !String(value ?? "").trim();

/**
 * Problems worth stopping the request for, as messages ready to show.
 *
 * Returns `[]` when the form can be submitted. Order is the order of the form,
 * so the first message points at the first thing to fix.
 */
export const validateBankAccountForm = (country, form) => {
  const errors = [];

  if (isBlank(form.personId) || !/^\d+$/.test(String(form.personId).trim())) {
    errors.push("El ID del propietario debe ser un número.");
  }
  if (isBlank(form.identifier)) {
    errors.push("Falta el ID de la propiedad.");
  } else if (!/^[a-zA-Z0-9_]+$/.test(String(form.identifier).trim())) {
    errors.push("El ID de la propiedad solo admite letras, números y guion bajo.");
  }
  if (isBlank(form.bankId)) errors.push("Selecciona un banco.");
  if (isBlank(form.accountType)) errors.push("Selecciona un tipo de cuenta.");

  const accountNumber = digitsOnly(form.accountNumber);
  if (isBlank(form.accountNumber)) {
    errors.push("Falta el número de cuenta.");
  } else if (!/^\d+$/.test(accountNumber)) {
    errors.push("El número de cuenta solo admite dígitos.");
  } else if (country === "MX" && accountNumber.length > MX_ACCOUNT_NUMBER_MAX_LENGTH) {
    errors.push(`El número de cuenta no puede superar los ${MX_ACCOUNT_NUMBER_MAX_LENGTH} dígitos.`);
  }

  // Optional in MX: the account is created with a generated CLABE when it is
  // left empty. A partially typed one, though, is a typo, not an omission.
  if (hasClabe(country) && !isBlank(form.clabeNumber)) {
    const clabe = digitsOnly(form.clabeNumber);
    if (!/^\d+$/.test(clabe) || clabe.length !== CLABE_LENGTH) {
      errors.push(`La CLABE debe tener exactamente ${CLABE_LENGTH} dígitos.`);
    }
  }

  if (isBlank(form.holder)) errors.push("Falta el titular de la cuenta.");
  if (hasDocumentType(country) && isBlank(form.documentType)) {
    errors.push("Selecciona el tipo de documento.");
  }
  if (isBlank(form.document)) errors.push(`Falta el ${DOCUMENT_LABEL[country].toLowerCase()}.`);
  if (isBlank(form.email) || !/^\S+@\S+\.\S+$/.test(String(form.email).trim())) {
    errors.push("El email no tiene un formato válido.");
  }
  if (hasCity(country) && isBlank(form.city)) errors.push("Falta la ciudad.");

  return errors;
};

/**
 * The request body for the country's POST, with nothing it does not accept.
 *
 * The endpoints validate strictly: an extra key is a 422, so `document_type`,
 * `clabe_number` and `city` are only included where they belong.
 */
export const buildBankAccountPayload = (country, form) => {
  const payload = {
    bank_id: String(form.bankId).trim(),
    identifier: String(form.identifier).trim(),
    account_number: digitsOnly(form.accountNumber),
    account_type: form.accountType,
    holder: String(form.holder).trim(),
    document: String(form.document).trim(),
    email: String(form.email).trim().toLowerCase(),
  };

  if (hasDocumentType(country)) payload.document_type = form.documentType;
  if (hasCity(country)) payload.city = String(form.city).trim();
  if (hasClabe(country) && !isBlank(form.clabeNumber)) {
    payload.clabe_number = digitsOnly(form.clabeNumber);
  }

  return payload;
};

/**
 * Bank options for a `<select>`, from the `/banks` response.
 *
 * CL returns `id`, CO and MX return `bank_id`, and the POST takes whichever one
 * that country produced. Sorting by name matters: the catalogs come back in
 * insertion order, which is not something an operator can scan.
 */
export const parseBanks = (response) =>
  ((response && response.banks) || [])
    .filter(Boolean)
    .map((bank) => ({
      value: String(bank.bank_id ?? bank.id ?? ""),
      label: bank.name,
      code: bank.code,
    }))
    .filter((bank) => bank.value && bank.label)
    .sort((a, b) => a.label.localeCompare(b.label));
